export interface PrivateFolderStorageUsage {
  userId: string;
  name: string | null;
  email: string;
  avatarUrl: string | null;
  privateFolderId: string;
  usedBytes: number;
  limitBytes: number | null;
  limitSource: 'workspace' | 'group' | 'user';
  limitSourceGroupId: string | null;
  limitSourceGroupName: string | null;
  usagePercent: number;
  docCount: number;
  blobCount: number;
}

export interface PrivateFolderStorageGroupLimit {
  groupId: string;
  name: string;
  isAutoSync: boolean;
  autoSyncFilter: string | null;
  memberCount: number;
  limitBytes: number | null;
}

export interface PrivateFolderStorageUserOverride {
  userId: string;
  limitBytes: number;
}

export interface WorkspacePrivateStoragePolicy {
  defaultLimitBytes: number;
  groupLimits: PrivateFolderStorageGroupLimit[];
  userOverrides: PrivateFolderStorageUserOverride[];
  members: PrivateFolderStorageUsage[];
}

export const getMyPrivateFolderStorageCacheKey = (
  serverScope: string,
  workspaceId: string,
  accountId: string
) =>
  `streamlain:private-folder-storage:me:${serverScope}:${workspaceId}:${accountId}`;

export const getWorkspacePrivateFolderStorageCacheKey = (
  serverScope: string,
  workspaceId: string,
  accountId: string
) =>
  `streamlain:private-folder-storage:policy:${serverScope}:${workspaceId}:${accountId}`;

async function requestPrivateStorage<T>(
  workspaceId: string,
  path: string,
  init: RequestInit | undefined,
  key: string
): Promise<T> {
  return defaultWorkspaceApiClient.requestJson<T>({
    workspaceId,
    path,
    init,
    category: 'settings',
    key,
    mode: init?.method ? 'foreground' : 'background',
    priority: init?.method ? 'foreground' : 'visible',
  });
}

export async function fetchMyPrivateFolderStorage(
  workspaceId: string,
  signal?: AbortSignal
): Promise<PrivateFolderStorageUsage | null> {
  return requestPrivateStorage<PrivateFolderStorageUsage | null>(
    workspaceId,
    `/api/workspace/${workspaceId}/storage/private/me`,
    {
      signal,
    },
    'private-storage:me'
  );
}

export async function fetchWorkspacePrivateFolderStorage(
  workspaceId: string,
  signal?: AbortSignal
): Promise<WorkspacePrivateStoragePolicy> {
  return requestPrivateStorage<WorkspacePrivateStoragePolicy>(
    workspaceId,
    `/api/workspace/${workspaceId}/storage/private`,
    {
      signal,
    },
    'private-storage:policy'
  );
}

export async function updateWorkspacePrivateFolderStorageDefaultLimit(
  workspaceId: string,
  limitBytes: number
): Promise<WorkspacePrivateStoragePolicy> {
  return requestPrivateStorage<WorkspacePrivateStoragePolicy>(
    workspaceId,
    `/api/workspace/${workspaceId}/storage/private/default`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ limitBytes }),
    },
    'private-storage:update-default'
  );
}

export async function updateWorkspacePrivateFolderStorageGroupLimit(
  workspaceId: string,
  groupId: string,
  limitBytes: number
): Promise<WorkspacePrivateStoragePolicy> {
  return requestPrivateStorage<WorkspacePrivateStoragePolicy>(
    workspaceId,
    `/api/workspace/${workspaceId}/storage/private/groups/${groupId}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ limitBytes }),
    },
    `private-storage:update-group:${groupId}`
  );
}

export async function resetWorkspacePrivateFolderStorageGroupLimit(
  workspaceId: string,
  groupId: string
): Promise<WorkspacePrivateStoragePolicy> {
  return requestPrivateStorage<WorkspacePrivateStoragePolicy>(
    workspaceId,
    `/api/workspace/${workspaceId}/storage/private/groups/${groupId}/reset`,
    {
      method: 'PATCH',
    },
    `private-storage:reset-group:${groupId}`
  );
}

export async function updateWorkspacePrivateFolderStorageLimit(
  workspaceId: string,
  userId: string,
  limitBytes: number
): Promise<PrivateFolderStorageUsage> {
  return requestPrivateStorage<PrivateFolderStorageUsage>(
    workspaceId,
    `/api/workspace/${workspaceId}/storage/private/${userId}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ limitBytes }),
    },
    `private-storage:update-user:${userId}`
  );
}

export async function resetWorkspacePrivateFolderStorageLimit(
  workspaceId: string,
  userId: string
): Promise<PrivateFolderStorageUsage> {
  return requestPrivateStorage<PrivateFolderStorageUsage>(
    workspaceId,
    `/api/workspace/${workspaceId}/storage/private/${userId}/reset`,
    {
      method: 'PATCH',
    },
    `private-storage:reset-user:${userId}`
  );
}
import { defaultWorkspaceApiClient } from '@affine/core/modules/cloud';
